<script setup lang="ts">
import type { SiteBlock } from '../../types/site'

/**
 * Renders an ordered list of site blocks. Shared by the public page view
 * and the editor canvas so what tenants build is exactly what visitors
 * see. Each block is wrapped by <SiteBlockFrame> with its width / text-size
 * / background / spacing settings; the block components themselves are pure
 * (data + settings props).
 *
 * Block type → component resolution comes from the shared registry
 * (composables/useSiteBlockRegistry.ts), populated at app init: this layer's
 * plugin registers the generic blocks, and each app registers its own
 * domain-specific blocks. The renderer stays agnostic of which concrete block
 * components exist — which is also what lets `columns` recurse into it for
 * each column without an import cycle.
 *
 * `nested` is passed through when rendering a column's blocks. `editing` is
 * passed through the same way, to `SiteBlockFrame` — see its doc comment.
 * Neither is set by the public page path, so this stays the exact plain
 * render a visitor gets unless a caller (the editor's Preview mode) opts in.
 */
defineProps<{ blocks: SiteBlock[], nested?: boolean, editing?: boolean }>()
</script>

<template>
  <div>
    <SiteBlockFrame
      v-for="block in blocks"
      :key="block.id"
      :block="block"
      :nested="nested"
      :editing="editing"
    />
  </div>
</template>
